import { jsPDF } from "jspdf";
import type { Track, TransitionSuggestion } from "../types";

function fmtDur(secs: number | null | undefined): string {
  if (secs == null || !Number.isFinite(secs)) return "—";
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
}

function fmtTotal(secs: number | null | undefined): string {
  if (secs == null || !Number.isFinite(secs)) return "—";
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = Math.floor(secs % 60);
  if (h > 0) return `${h}h ${m}m`;
  return `${m}m ${s}s`;
}

/**
 * Client-side PDF export of the suggested set order and transition notes.
 */
export function downloadSetPdf(params: {
  orderedTracks: Track[];
  transitions: TransitionSuggestion[];
  totalDurationSeconds: number | null;
  profile: string;
  title?: string;
}): void {
  const {
    orderedTracks,
    transitions,
    totalDurationSeconds,
    profile,
    title = "SetPilot — suggested set",
  } = params;

  const doc = new jsPDF({ unit: "pt", format: "a4" });
  const margin = 48;
  const pageBottom = 780;
  let y = margin;

  const ensureSpace = (needed: number) => {
    if (y + needed > pageBottom) {
      doc.addPage();
      y = margin;
    }
  };

  const line = (text: string, fontSize: number, style: "normal" | "bold" = "normal") => {
    ensureSpace(fontSize + 8);
    doc.setFont("helvetica", style);
    doc.setFontSize(fontSize);
    const lines = doc.splitTextToSize(text, 515);
    doc.text(lines, margin, y);
    y += lines.length * (fontSize * 1.15);
  };

  line(title, 16, "bold");
  line(`Profile: ${profile}`, 10);
  line(`Approx. duration: ${fmtTotal(totalDurationSeconds)}`, 10);
  y += 6;

  orderedTracks.forEach((track, idx) => {
    ensureSpace(36);
    const name = track.originalFilename.replace(/\.[^.]+$/, "");
    line(`${String(idx + 1).padStart(2, "0")}. ${name}`, 11, "bold");
    line(
      `    BPM ${track.bpm != null ? String(track.bpm) : "—"} · ${track.camelotCode ?? "—"} · ${[
        track.musicalKey,
        track.keyMode,
      ]
        .filter(Boolean)
        .join(" ")} · ${fmtDur(track.durationSeconds)}`,
      9
    );

    const next = orderedTracks[idx + 1];
    if (next) {
      const tr = transitions.find(
        (t) => t.fromTrackId === track.id && t.toTrackId === next.id
      );
      if (tr) {
        line(
          `    → ${tr.technique} (${tr.keyCompatibility})${tr.why ? ` — ${tr.why}` : tr.note ? ` — ${tr.note}` : ""}`,
          8
        );
      }
    }
    y += 4;
  });

  doc.save("setpilot-set.pdf");
}
